import { EmpreintesCarboneData } from '@services/interfaces/generated/YSER-v1';
import { useMemo, useState } from 'react';

export interface TelephoneFilters {
  marques: string[];
  modeles: string[];
  dateDebut: string;
  dateFin: string;
}

const initialFilters: TelephoneFilters = {
  marques: [],
  modeles: [],
  dateDebut: '',
  dateFin: '',
};

export const useEmpreinteCarboneTelFilters = (data: EmpreintesCarboneData | undefined) => {
  const detailsMemo = useMemo(() => data?.details || [], [data]);

  // État des filtres appliqués (initialement aucun filtre)
  const [appliedFilters, setAppliedFilters] = useState<TelephoneFilters>(initialFilters);

  // État des filtres en cours de sélection dans le modal
  const [pendingFilters, setPendingFilters] = useState<TelephoneFilters>(initialFilters);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Extraire toutes les marques uniques des données et ajouter "Autres" si nécessaire
  const availableMarques = useMemo(() => {
    // Calculer l'empreinte totale par marque
    const marquesEmpreinte = new Map<string, number>();

    detailsMemo.forEach((item) => {
      if (item.marque) {
        marquesEmpreinte.set(item.marque, (marquesEmpreinte.get(item.marque) || 0) + item.totalEmpreinteCarbone);
      }
    });

    // Trier par empreinte carbone (décroissante) et prendre les 3 premiers
    const sortedMarques = Array.from(marquesEmpreinte.entries())
      .sort(([, empreinteA], [, empreinteB]) => empreinteB - empreinteA)
      .map(([marque]) => marque);

    const topMarques = sortedMarques.slice(0, 3);
    const otherMarques = sortedMarques.slice(3);

    // Construire la liste finale
    const result = topMarques.sort();
    if (otherMarques.length > 0) {
      result.push('Autres');
    }

    return result;
  }, [detailsMemo]);

  // Déterminer les top 3 marques pour la logique de filtrage
  const topMarquesList = useMemo(() => {
    const marquesEmpreinte = new Map<string, number>();

    detailsMemo.forEach((item) => {
      if (item.marque) {
        marquesEmpreinte.set(item.marque, (marquesEmpreinte.get(item.marque) || 0) + item.totalEmpreinteCarbone);
      }
    });

    return Array.from(marquesEmpreinte.entries())
      .sort(([, empreinteA], [, empreinteB]) => empreinteB - empreinteA)
      .slice(0, 3)
      .map(([marque]) => marque);
  }, [detailsMemo]); // Extraire les modèles basés sur les marques sélectionnées dans le modal
  const availableModeles = useMemo(() => {
    if (pendingFilters.marques.length === 0) {
      // Si aucune marque n'est sélectionnée, afficher tous les modèles
      const modeles = new Set<string>();
      detailsMemo.forEach((item) => {
        if (item.modele) {
          modeles.add(item.modele);
        }
      });
      return Array.from(modeles).sort();
    }

    // Sinon, filtrer par marques sélectionnées
    const modeles = new Set<string>();
    detailsMemo.forEach((item) => {
      if (item.modele && item.marque) {
        // Si "Autres" est sélectionné, inclure tous les modèles des marques non-top3
        if (pendingFilters.marques.includes('Autres') && !topMarquesList.includes(item.marque)) {
          modeles.add(item.modele);
        }
        // Sinon, inclure les modèles des marques sélectionnées
        else if (pendingFilters.marques.includes(item.marque)) {
          modeles.add(item.modele);
        }
      }
    });
    return Array.from(modeles).sort();
  }, [detailsMemo, pendingFilters.marques, topMarquesList]);

  // Appliquer les filtres aux données (utilise appliedFilters, pas pendingFilters)
  const filteredData = useMemo(() => {
    if (!data) return undefined;

    const filteredDetails = detailsMemo.filter((item) => {
      // Filtre par date
      if (appliedFilters.dateDebut) {
        const itemDate = new Date(item.dateAchat);
        const dateDebut = new Date(appliedFilters.dateDebut);
        if (itemDate < dateDebut) return false;
      }
      if (appliedFilters.dateFin) {
        const itemDate = new Date(item.dateAchat);
        const dateFin = new Date(appliedFilters.dateFin);
        if (itemDate > dateFin) return false;
      }

      // Filtre par marque
      if (appliedFilters.marques.length > 0 && item.marque) {
        let marqueToCheck = item.marque;
        // Si la marque n'est pas dans le top 3, la traiter comme "Autres"
        if (!topMarquesList.includes(item.marque)) {
          marqueToCheck = 'Autres';
        }
        if (!appliedFilters.marques.includes(marqueToCheck)) {
          return false;
        }
      }

      // Filtre par modèle
      if (appliedFilters.modeles.length > 0 && item.modele) {
        if (!appliedFilters.modeles.includes(item.modele)) {
          return false;
        }
      }

      return true;
    });

    return {
      ...data,
      data: {
        ...data,
        details: filteredDetails,
      },
    };
  }, [data, detailsMemo, appliedFilters, topMarquesList]);

  const updatePendingFilters = (newFilters: Partial<TelephoneFilters>) => {
    setPendingFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const applyFilters = () => {
    setAppliedFilters(pendingFilters);
    setIsFilterModalOpen(false);
  };

  const resetFilters = () => {
    setAppliedFilters(initialFilters);
    setPendingFilters(initialFilters);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      appliedFilters.marques.length > 0 ||
      appliedFilters.modeles.length > 0 ||
      appliedFilters.dateDebut !== '' ||
      appliedFilters.dateFin !== ''
    );
  }, [appliedFilters]);

  return {
    filters: pendingFilters,
    appliedFilters,
    filteredData,
    availableMarques,
    availableModeles,
    updatePendingFilters,
    applyFilters,
    resetFilters,
    hasActiveFilters,
    isFilterModalOpen,
    setIsFilterModalOpen,
  };
};
